/**
 * Die ausgelieferte Fassung — eine Quelle für alles, was sie kennen muss.
 *
 * `DOCUMENTATION.md` §1 verlangt eine fortgeschriebene Versionsnummer und
 * einen Tag je Release; der Rollout richtet sich danach. Damit die Nummer
 * nicht an drei Stellen auseinanderläuft, steht sie hier und wird von
 * `release.test.ts` gegen **jede** `package.json` des Arbeitsbereichs und
 * gegen den datierten Abschnitt im CHANGELOG geprüft.
 *
 * Was hier **nicht** steht: der Git-Kurz-Hash. Er ändert sich je Commit und
 * wäre als eingecheckte Zeile bei jedem Bau schmutzig. Er kommt beim Bauen
 * dazu (`NADIA_BUILD`) und steht in der Anwendung neben der Nummer — so
 * lassen sich zwei Stände derselben Nummer unterscheiden.
 */
export const VERSION = '0.9.1';
/**
 * Jüngste Fassung zuerst. Der oberste Eintrag gehört zu `VERSION`; ein Test
 * hält beides zusammen, damit ein halb ausgeführtes Release auffällt.
 */
export const FASSUNGEN = [
    {
        version: '0.9.1',
        datum: '2026-10-12',
        punkte: [
            'Neuer Name: VeeKy — Voice Kommunikation made Easy.',
            'Neues Logo und neue Farben in allen Oberflächen und in der App.',
        ],
    },
    {
        version: '0.9.0',
        datum: '2026-09-15',
        punkte: [
            'Die Verwaltung koppelt sich selbst mit dem Code, den der Partner hinterlegt — der Server ruft nirgendwohin.',
            'Kein gemeinsames Geheimnis mehr zwischen Verwaltung und Stack: jede Anfrage ist signiert.',
        ],
    },
    {
        version: '0.8.0',
        datum: '2026-09-15',
        punkte: [
            'Ein neuer Partner-Stack richtet sich mit einem Kopplungscode selbst ein — erstes Konto durch den Partner.',
            'Die Verwaltung sieht je Partner den Server, seine Fassung und die Hinterlegung für den Notfall.',
        ],
    },
    {
        version: '0.7.2',
        datum: '2026-09-15',
        punkte: ['Nachbesserung der Rechte für die Hausarbeit.'],
    },
    {
        version: '0.7.1',
        datum: '2026-09-15',
        punkte: ['Die Hausarbeit läuft auch bei übernommenen Kundendatenbanken durch.'],
    },
    {
        version: '0.7.0',
        datum: '2026-09-15',
        punkte: [
            'Abgelaufene Sitzungen und Links werden stündlich aufgeräumt.',
            'Das Nachweisprotokoll wird monatlich abgeschlossen; seine Prüfsummen liegen an zwei Orten.',
            'Auch das Protokoll der Verwaltung ist jetzt gegen nachträgliche Änderung gesichert.',
        ],
    },
    {
        version: '0.6.9',
        datum: '2026-09-15',
        punkte: ['Der zweite Faktor lässt sich nicht mehr durchprobieren — zehn Versuche, dann Pause.'],
    },
    {
        version: '0.6.8',
        datum: '2026-09-15',
        punkte: ['Nicht ausgeliefert — der Bau schlug fehl. Inhalt siehe 0.6.9.'],
    },
    {
        version: '0.6.7',
        datum: '2026-09-15',
        punkte: ['Der Kontaktimport nimmt auch große Dateien an — bis 2000 Zeilen, wie zugesagt.'],
    },
    {
        version: '0.6.6',
        datum: '2026-09-15',
        punkte: [
            'Auch in Kundenverwaltung und Verwaltung wirkt eine geänderte Rolle sofort — ohne Neuladen.',
        ],
    },
    {
        version: '0.6.5',
        datum: '2026-09-15',
        punkte: ['Entzieht Ihr Partner ein Recht, verschwindet die Schaltfläche beim nächsten Klick.'],
    },
    {
        version: '0.6.4',
        datum: '2026-09-15',
        punkte: ['Die Anmeldeseite fragt nicht mehr im Takt nach einer Sitzung, die es nicht gibt.'],
    },
    {
        version: '0.6.3',
        datum: '2026-09-15',
        punkte: [
            'Die App erreicht den Dienst auch in Netzen mit Sicherheits-Appliance.',
            'Vergibt oder entzieht Ihr Partner ein Recht, wirkt es sofort — ohne die Seite neu zu laden.',
        ],
    },
    {
        version: '0.6.2',
        datum: '2026-09-15',
        punkte: [
            'Die App findet ihre Gerätekopplung wieder — sie meldete sich fälschlich als nicht registriert.',
            'Schaltet Ihr Partner die Selbstverwaltung ab, gilt das sofort für alle Anwender.',
        ],
    },
    {
        version: '0.6.1',
        datum: '2026-09-15',
        punkte: ['Beim Import ist die Spaltenzuordnung vollständig sichtbar.'],
    },
    {
        version: '0.6.0',
        datum: '2026-09-15',
        punkte: [
            'Kontakte: eigene und zentrale, mit Suche, Anlegen und Ändern.',
            'Kontakte aus einer CSV-Datei importieren und die Spalten selbst zuordnen.',
            'Beim Anruf zeigt die App Name, Firma und Position statt nur der Nummer.',
        ],
    },
    {
        version: '0.5.2',
        datum: '2026-09-14',
        punkte: ['Weniger Erklärtext unter den Feldern.'],
    },
    {
        version: '0.5.1',
        datum: '2026-09-14',
        punkte: ['Der zweite Faktor für alle ist ein Schalter — Verwalter brauchen ihn ohnehin immer.'],
    },
    {
        version: '0.5.0',
        datum: '2026-09-14',
        punkte: [
            'Ihr Partner kann den zweiten Faktor für alle Anwender Ihres Unternehmens verlangen.',
            'Ist er verlangt, lässt er sich im Unternehmen nicht mehr abschalten.',
        ],
    },
    {
        version: '0.4.0',
        datum: '2026-09-14',
        punkte: [
            'Die Anmeldung in Kundenverwaltung und Verwaltung endet nach acht Stunden.',
            'Die E-Mail eines Anwenders lässt sich jederzeit nachtragen oder wieder entfernen.',
        ],
    },
    {
        version: '0.3.0',
        datum: '2026-09-14',
        punkte: [
            'Zweiter Faktor für die Anmeldung im Browser: ein Code aus Ihrer Authenticator-App.',
            'Für Anwender mit Selbstverwaltung ist er Pflicht, für alle anderen freiwillig.',
            'Wer die Seite über das Symbol in der Taskleiste öffnet, braucht ihn nicht.',
        ],
    },
    {
        version: '0.2.0',
        datum: '2026-09-14',
        punkte: [
            'Anmeldung im Browser: mit E-Mail und Passwort von jedem Gerät, auch ohne die App.',
            'Das erste Passwort setzen Sie über einen Link, den Ihre Administration ausstellt.',
            'Die Sitzung im Browser endet nach einem Arbeitstag und wird über das Symbol in der Taskleiste neu geöffnet.',
        ],
    },
    {
        version: '0.1.0',
        datum: '2026-09-14',
        punkte: [
            'Wählen aus jeder Anwendung: Rufnummer markieren, Tastenkürzel drücken, das Telefon klingelt.',
            'Anrufverlauf mit Ausgang und Dauer, je Anwender.',
            'Kontaktdatenbank: eigene und zentrale Kontakte, Namen zu Rufnummern.',
            'Anrufauswertung: Erreichbarkeit, Wartezeiten, Stoßzeiten je Anlage, Channel und Mitarbeiter.',
            'Kundenverwaltung für Partner: Kunden, Anwender, Geräte, Editionen und Lizenzen.',
            'Verwaltung für netable: Partner, Editionen mit Preisen, Mengen je Tag und Abrechnung je Monat.',
            'Die Sitzung im Browser endet nach einem Arbeitstag und wird über das Symbol in der Taskleiste neu geöffnet.',
        ],
    },
];
/**
 * Was die Anwendung anzeigt: `0.1.0` oder `0.1.0 · a1b2c3d`.
 *
 * Der Kurz-Hash kommt aus der Umgebung (`NADIA_BUILD` im Dienst,
 * `VITE_NADIA_BUILD` beim Bauen der Oberflächen). Fehlt er, bleibt es bei der
 * Nummer — eine erfundene Kennung wäre schlimmer als keine.
 */
export function fassungsschild(build) {
    const kurz = build?.trim();
    return kurz ? `${VERSION} · ${kurz}` : VERSION;
}
//# sourceMappingURL=release.js.map